"use client";

import { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

export default function HowItWorksSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const steps = [
    {
      id: 'step_1',
      number: '01',
      icon: 'DocumentTextIcon' as const,
      title: 'Complete Personality Assessment',
      description: '10-minute questionnaire covering values, lifestyle, communication style, and relationship goals. Be honest - our AI learns from your authentic self.',
      color: 'primary',
    },
    {
      id: 'step_2',
      number: '02',
      icon: 'CpuChipIcon' as const,
      title: 'AI Analyzes 50+ Factors',
      description: 'Our algorithm doesn\'t just match interests. It identifies deep compatibility: emotional availability, conflict resolution style, life stage alignment.',
      color: 'secondary',
    },
    {
      id: 'step_3',
      number: '03',
      icon: 'UsersIcon' as const,
      title: 'Receive 3 Curated Matches Daily',
      description: 'Quality over quantity. Each match is thoughtfully selected with detailed compatibility breakdown. No mindless swiping - just potential.',
      color: 'accent',
    },
    {
      id: 'step_4',
      number: '04',
      icon: 'VideoCameraIcon' as const,
      title: 'Video Chat Before Meeting',
      description: 'Built-in video dating. Get to know each other face-to-face from the comfort of home. Meet in person only when you\'re both ready.',
      color: 'success',
    },
  ];

  return (
    <section
      id="how-it-works"
      ref={sectionRef}
      className="py-24 px-6 bg-muted/10 border-y border-white/5"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2
            className={`text-4xl md:text-6xl font-bold tracking-tight text-foreground mb-6 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            How <span className="text-primary">MeetX</span> Works
          </h2>
          <p
            className={`text-lg text-muted-foreground font-light transition-all duration-700 delay-100 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            From signup to your first date, we&apos;ve designed every step to maximize compatibility and minimize wasted time.
          </p>
        </div>

        {/* Steps Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {steps.map((step, index) => (
            <div
              key={step.id}
              className={`glass-card-hover p-8 rounded-2xl transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              {/* Step Number */}
              <div className="flex items-start justify-between mb-6">
                <span className="text-6xl font-bold text-white/5">{step.number}</span>
                <div
                  className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                    step.color === 'primary' ?'bg-primary/10'
                      : step.color === 'secondary' ?'bg-secondary/10': step.color === 'accent' ?'bg-accent/10' :'bg-success/10'
                  }`}
                >
                  <Icon
                    name={step.icon}
                    variant="solid"
                    size={28}
                    className={
                      step.color === 'primary' ?'text-primary'
                        : step.color === 'secondary' ?'text-secondary'
                        : step.color === 'accent' ?'text-accent' :'text-success'
                    }
                  />
                </div>
              </div>

              {/* Content */}
              <h3 className="text-2xl font-bold text-foreground mb-4">{step.title}</h3>
              <p className="text-muted-foreground leading-relaxed">{step.description}</p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <a
            href="#waitlist"
            className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-primary to-secondary text-white font-semibold rounded-full hover:shadow-2xl hover:shadow-primary/50 transition-all group"
          >
            Start Your Journey
            <Icon
              name="ArrowRightIcon"
              size={20}
              className="group-hover:translate-x-1 transition-transform"
            />
          </a>
        </div>
      </div>
    </section>
  );
}